import { useCallback, useEffect, useRef, useState } from 'react';
import { useFirebase } from 'v3/hooks/useFirebase';
import { SseStatus } from './types';

export interface UseSseReturn<T = any> {
  data: T | null;
  status: SseStatus;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  reset: () => void;
}

export const useSse = <T = any>(url: string, desiredResult?: string): UseSseReturn<T> => {
  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<SseStatus>('closed');
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const { getToken } = useFirebase();
  const baseUrl = import.meta.env.VITE_FINANCING_API_BASE_URL;

  const buildUrl = useCallback(async () => {
    const token = await getToken();
    return `${baseUrl}${url}/?token=${token}`;
  }, [baseUrl, url, getToken]);

  const connect = useCallback(async () => {
    if (!url || eventSourceRef.current) return;

    setData(null);
    setError(null);
    setStatus('connecting');

    try {
      const authenticatedUrl = await buildUrl();
      const eventSource = new EventSource(authenticatedUrl);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setStatus('open');
      };

      eventSource.onmessage = (event: MessageEvent) => {
        if (event.data && !event.data.includes('heartbeat')) {
          try {
            const messagePayload = JSON.parse(event.data) as T;
            setData(messagePayload);
            if (Boolean(desiredResult) && event.data.includes(desiredResult)) {
              eventSource.close();
              eventSourceRef.current = null;
              setStatus('closed');
            }
          } catch {
            setError('Failed to parse SSE data.');
            eventSource.close();
            eventSourceRef.current = null;
            setStatus('closed');
          }
        }
      };

      eventSource.onerror = () => {
        setError('Error in SSE connection.');
        setStatus('closed');
      };
    } catch {
      setError('Error initializing SSE.');
      setStatus('closed');
    }
  }, [url, desiredResult, buildUrl]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setStatus('closed');
    }
  }, []);

  const reset = useCallback(() => {
    disconnect();
    setData(null);
    setError(null);
    setStatus('closed');
  }, [disconnect]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  useEffect(() => {
    reset();
  }, [url, reset]);

  return { data, status, error, connect, disconnect, reset };
};
